import { useEffect, useState } from 'react';
import {
  AlertCircle,
  Hash,
  Loader2,
  MessageSquareQuote,
  Save,
  Sparkles,
  Star,
  X,
} from 'lucide-react';
import { ImageUploadField } from '../../components/ui/ImageUploadField';
import { useCreateTestimonial, useUpdateTestimonial } from '../../services/httpServices/queries';
import type { WebsiteTestimonial } from '../../services/httpServices/websiteService';

interface WebsiteTestimonialModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The testimonial being edited, or null/undefined when adding a new one. */
  testimonial?: WebsiteTestimonial | null;
  onSaved?: () => void;
}

interface TestimonialForm {
  name: string;
  role: string;
  title: string;
  quote: string;
  image: string;
  rating: number;
  isActive: boolean;
  displayOrder: number;
}

const EMPTY_FORM: TestimonialForm = {
  name: '',
  role: '',
  title: '',
  quote: '',
  image: '',
  rating: 5,
  isActive: true,
  displayOrder: 0,
};

function validate(form: TestimonialForm): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!form.name.trim()) errors.name = 'A name is required.';
  if (!form.quote.trim()) errors.quote = 'A quote is required.';
  return errors;
}

/**
 * Add/edit dialog for a single patient testimonial. Testimonials are rendered
 * on the public `/testimonials` page, so the right-hand pane previews exactly
 * how the card will appear.
 */
export function WebsiteTestimonialModal({
  isOpen,
  onClose,
  testimonial,
  onSaved,
}: WebsiteTestimonialModalProps) {
  const isEdit = !!testimonial?.id;
  const createMutation = useCreateTestimonial();
  const updateMutation = useUpdateTestimonial();

  const [form, setForm] = useState<TestimonialForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setForm(
      testimonial
        ? {
            name: testimonial.name ?? '',
            role: testimonial.role ?? '',
            title: testimonial.title ?? '',
            quote: testimonial.quote ?? '',
            image: testimonial.image ?? '',
            rating: testimonial.rating ?? 5,
            isActive: testimonial.isActive,
            displayOrder: testimonial.displayOrder ?? 0,
          }
        : EMPTY_FORM,
    );
    setErrors({});
    setGeneralError('');
  }, [isOpen, testimonial]);

  const setField = <K extends keyof TestimonialForm>(field: K, value: TestimonialForm[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fieldErrors = validate(form);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    setGeneralError('');
    const payload = {
      name: form.name.trim(),
      role: form.role.trim() || undefined,
      title: form.title.trim() || undefined,
      quote: form.quote.trim(),
      image: form.image.trim() || undefined,
      rating: Number(form.rating) || 5,
      isActive: form.isActive,
      displayOrder: Number(form.displayOrder) || 0,
    };

    try {
      if (isEdit && testimonial) {
        await updateMutation.mutateAsync({ id: testimonial.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onSaved?.();
      onClose();
    } catch (err: any) {
      setGeneralError(
        err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.',
      );
    }
  };

  if (!isOpen) return null;

  const inputBase =
    'w-full px-3 py-2 border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 transition-colors';
  const ic = (field: string) =>
    `${inputBase} ${errors[field] ? 'border-danger focus:ring-danger' : 'border-border focus:ring-primary'}`;
  const labelClass = 'block text-sm font-medium text-text mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border bg-background/50 rounded-t-2xl sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <MessageSquareQuote size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text">
                {isEdit ? 'Edit testimonial' : 'Add testimonial'}
              </h2>
              <p className="text-xs text-text-muted">
                Shown as a card on the public /testimonials page.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-background rounded-xl transition-colors"
            aria-label="Close"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {generalError && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-danger/10 border border-danger/20 text-danger text-sm">
              <AlertCircle size={16} />
              <span>{generalError}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Left: fields */}
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Author name *</label>
                <input
                  type="text"
                  autoFocus
                  className={ic('name')}
                  placeholder="e.g. Charlotte Walker"
                  value={form.name}
                  maxLength={150}
                  onChange={(e) => setField('name', e.target.value)}
                />
                {errors.name && <p className="text-xs text-danger mt-1">{errors.name}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Role / title</label>
                  <input
                    type="text"
                    className={ic('role')}
                    placeholder="e.g. Founder"
                    value={form.role}
                    maxLength={150}
                    onChange={(e) => setField('role', e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelClass}>Heading</label>
                  <input
                    type="text"
                    className={ic('title')}
                    placeholder="e.g. Patient Centered Care"
                    value={form.title}
                    maxLength={255}
                    onChange={(e) => setField('title', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Quote *</label>
                <textarea
                  rows={4}
                  className={ic('quote')}
                  placeholder="What did the patient say?"
                  value={form.quote}
                  onChange={(e) => setField('quote', e.target.value)}
                />
                {errors.quote && <p className="text-xs text-danger mt-1">{errors.quote}</p>}
              </div>

              <div>
                <label className={labelClass}>Avatar</label>
                <ImageUploadField
                  value={form.image}
                  onChange={(url) => setField('image', url)}
                  previewClassName="h-36"
                  helperText="Optional — upload a photo of the patient."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Rating</label>
                  <div className="flex items-center gap-1.5">
                    {Array.from({ length: 5 }).map((_, index) => {
                      const value = index + 1;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setField('rating', value)}
                          aria-label={`${value} star`}
                          className="p-0.5"
                        >
                          <Star
                            size={20}
                            className={value <= form.rating ? 'text-warning' : 'text-text-muted/40'}
                            fill={value <= form.rating ? 'currentColor' : 'none'}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className={labelClass}>
                    <span className="inline-flex items-center gap-1.5">
                      <Hash size={13} /> Display order
                    </span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    className={inputBase + ' border-border focus:ring-primary'}
                    value={form.displayOrder}
                    onChange={(e) => setField('displayOrder', Number(e.target.value))}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Status</label>
                <button
                  type="button"
                  onClick={() => setField('isActive', !form.isActive)}
                  className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    form.isActive
                      ? 'border-success/40 bg-success/10 text-success'
                      : 'border-border bg-background text-text-muted'
                  }`}
                >
                  <span>{form.isActive ? 'Active' : 'Inactive'}</span>
                  <span
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                      form.isActive ? 'bg-success' : 'bg-border'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                        form.isActive ? 'translate-x-[18px]' : 'translate-x-0.5'
                      }`}
                    />
                  </span>
                </button>
              </div>
            </div>

            {/* Right: live preview */}
            <div className="space-y-2">
              <label className={labelClass}>
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles size={13} className="text-primary" /> Live preview
                </span>
              </label>
              <div className="rounded-xl border border-border bg-background p-5">
                <div className="flex items-center gap-0.5 text-warning mb-3">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Star
                      key={index}
                      size={16}
                      fill={index < form.rating ? 'currentColor' : 'none'}
                      className={index < form.rating ? 'text-warning' : 'text-text-muted/40'}
                    />
                  ))}
                </div>
                {form.title.trim() && (
                  <p className="font-semibold text-text mb-1">{form.title.trim()}</p>
                )}
                <p className="text-sm text-text-muted italic mb-4">
                  &ldquo;{form.quote.trim() || 'Your quote will appear here.'}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  {form.image ? (
                    <img
                      src={form.image}
                      alt={form.name || 'avatar'}
                      className="w-11 h-11 rounded-full object-cover border border-border"
                    />
                  ) : (
                    <span className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                      {(form.name.trim() || '?').charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div>
                    <p className="text-sm font-medium text-text">
                      {form.name.trim() || 'Anonymous'}
                    </p>
                    <p className="text-xs text-text-muted">{form.role.trim() || 'Patient'}</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-text-muted">
                {form.isActive
                  ? 'Visible on the public testimonials page.'
                  : 'Hidden from the public testimonials page.'}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {isEdit ? 'Save changes' : 'Add testimonial'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
